import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  FormControl,
  Fab,
  CircularProgress,
  Divider,
  Theme,
  IconButton,
  Chip
} from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";

import { TextField } from "formik-mui";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import { Field, FieldProps, Form, Formik } from 'formik';
import { useLocation, useNavigate } from "react-router-dom";
import { DateTime as d } from "luxon";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { useSelector } from "react-redux";

import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import { IDriverDocument, IDriverProfile } from ".";
import { useSnackBar } from "../../../common/SnackBarContext/SnackBarContext";
import { IAppState } from "../../../../store";
import { getDefaultCountryCode, getLocalizedDateFormat } from "../../../../utils/localized.syntex";
import { GET_USER_DRIVER_BY_ID } from "../../../../graphql/vehicleMovement/queries";
import { DvlaVerificationStatus } from "../../../../reducers/customer/const";
import { getSignedUrl } from "../../../../utils/getSignedUrl";
import { checkDeleteOrDownloadFileType, checkUploadFileFormat, DATE_TYPE, formatGraphQLErrorMessage, getLocalisedText, isImageFileType, LocaliseFormat, LocaliseType, returnSortedDocuments, uploadFileExtensionAndContentType, validateSingleFileExtension } from "../../../common/utils";
import { SnackBarVariant } from "../../../common/SnackbarWrapper/SnackbarWrapper";
import { VERIFY_DRIVER_LICENCE_DVLA } from "../../../../graphql/driver/verifyLicenseDvla";
import { DELETE_DRIVER_DOCUMENT } from "../../../../graphql/driver/deleteDriverDocument";
import { ADD_DRIVER_DOCUMENT } from "../../../../graphql/driver/addDriverDocument";
import { UPDATE_DRIVER_PROFILE } from "../../../../graphql/driver/updateDriverProfile";
import { CREATE_DRIVER_PROFILE } from "../../../../graphql/driver/createDriverProfile";
import { socket } from "../../../../utils/socket";
import { SOCKET_EVENTS } from "../../ReservationManagement/utils";
import { captureErrorException } from "../../../../utils/sentry";
import DvlaVerificationDialog, { IDvlaCheckValues } from "../../CustomerManagement/CustomerDetails/DvlaVerificationDialog";
import AddressPicker from "../../CustomerManagement/NewCustomer/AddressPicker/AddressPicker";
import { IAddress } from "../../../../reducers/user/types";
import { validateName } from "../../../common/ValidationRules";
import DragDropImages from "../../../common/DragAndDropFiles/DragAndDropImage/DragDropImages";
import { DocumentDialog } from "../../CustomerManagement/CustomerDetails/DocumentDialog";
import { NuvvenTable } from "../../../common/NuvvenTable/NuvvenTable";
import { ConfirmationDialog } from "../../../common/ConfirmationDialog/ConfirmationDialog";
import FlatPickerBar from "../../../common/FlatPicker";
import RefreshIcon from '@mui/icons-material/Refresh';

const initialDriverData: IDriverProfile = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  email: "",
  phoneNumber: {
    phone: "",
    country: "",
  },
  nationalInsuranceNumber: "",
  license: {
    licenseNumber: "",
    dateOfExpiry: "",
    dateOfIssue: "",
    issuingAuthority: "",
    endorsement: "",
    backImage: "",
    frontImage: "",
    offences: 0,
    points: 0,
    dvlaVerificationStatus: "",
  },
  documents: [],
  profilePicture: "",
  address: {
    street: "",
    city: "",
    fullAddress: "",
    state: "",
    country: "",
    zipcode: "",
    geometry: {
      type: "",
      latitude: 0,
      longitude: 0
    }
  }
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    saveButton: {
      backgroundColor: "#faaf40",
      border: 0,
      borderRadius: 26,
      fontSize: "14px",
      color: "white",
      minHeight: 43,
      minWidth: "auto",
      width: 150
    },
    img: {
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: "200px"
    },
  })
)


export const DriverProfile: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const styles = useStyles();
  const snackbar = useSnackBar();
  const userState = useSelector((state: IAppState) => state.userReducer);
  const { country } = userState.currentOrganisation.address;

  const [values, setValues] = useState<IDriverProfile>(initialDriverData);
  const [phoneError, setPhoneError] = useState<string>("");
  const [countryCode, setCountryCode] = useState<any>(getDefaultCountryCode(country));
  const [isFrontImageUpload, setFrontImageUpload] = useState<boolean>(false);
  const [isBackImageUpload, setBackImageUpload] = useState<boolean>(false);
  const [imageUploadInProgress, setImageUploadInProgress] = useState(false);
  const { _e_ } = useSelector((state: IAppState) => state.authReducer);
  const [frontLicenseImage, setFrontLicenseImage] = useState<any>("");
  const [backLicenseImage, setBackLicenseImage] = useState<any>("");
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [documents, setDocuments] = useState<IDriverDocument[]>([]);

  const [uploadedDocument, setUploadedDocument] = useState<IDriverDocument | null>(null);
  const [documentDialogVisible, setDocumentDialogVisible] = useState<boolean>(false);
  const [deleteDocumentUrl, setDeleteDocumentUrl] = useState<string>("");
  const [documentIndex, setDocumentIndex] = useState<number>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
  const [dvlaDialogVisible, setDvlaDialogVisible] = useState<boolean>(false);
  const [dvlaVerificationStatus, setDvlaVerificationStatus] = useState<string>("");

  enum DVLAStatus {
    VERIFIED = 'Verified',
    FAILED = 'Failed',
    EXPIRED = 'Expired',
    UNKNOWN = 'Unknown'
  }

  const userId = useMemo(() => {
    return new URLSearchParams(location.search).get("id");
  }, [location.search]);

  const checkDLExpiry = (expiryDate: string | null): DVLAStatus => {
    if (!expiryDate) return DVLAStatus.UNKNOWN;

    const today = d.now().startOf('day');
    const dlExpiry = d.fromISO(expiryDate).startOf('day');

    return dlExpiry < today ? DVLAStatus.EXPIRED : DVLAStatus.VERIFIED;
  };

  const [getUserDriver, { data: driverData, loading: isDriverLoading }] = useLazyQuery(GET_USER_DRIVER_BY_ID, {
    fetchPolicy: "network-only",
    async onCompleted(data: any) {
      if (data && data.getDriverUserById) {
        if (!data.getDriverUserById.driverProfile) {
          setValues({
            ...values,
            firstName: data.getDriverUserById?.firstName || "",
            lastName: data.getDriverUserById?.lastName || "",
            email: data.getDriverUserById?.email || "",
            phoneNumber: data.getDriverUserById?.phoneNumber || {}
          })
        } else {
          setValues((prevValues) => ({
            ...prevValues,
            id: data.getDriverUserById?.driverProfile?.id || "",
            firstName: data.getDriverUserById?.driverProfile?.firstName || data.getDriverUserById?.firstName || "",
            lastName: data.getDriverUserById?.driverProfile?.lastName || data.getDriverUserById?.lastName || "",
            email: data.getDriverUserById?.driverProfile?.email || data.getDriverUserById?.email || "",
            phoneNumber: data.getDriverUserById?.driverProfile?.phoneNumber || data.getDriverUserById?.phoneNumber || {},
            dateOfBirth: data.getDriverUserById?.driverProfile?.dateOfBirth || "",
            license: data.getDriverUserById?.driverProfile?.license || {},
            documents: data.getDriverUserById?.driverProfile?.documents ?? [],
            profilePicture: data.getDriverUserById?.driverProfile?.profilePicture || "",
            address: data.getDriverUserById?.driverProfile?.address || {},
            nationalInsuranceNumber: data.getDriverUserById?.driverProfile?.nationalInsuranceNumber || ""
          }));
          const licenseStatus = checkDLExpiry(data.getDriverUserById?.driverProfile?.license.dateOfExpiry)
          if (licenseStatus === DVLAStatus.EXPIRED) {
            setValues((prevValues) => ({
              ...prevValues,
              license: {
                ...prevValues.license,
                dvlaVerificationStatus: DvlaVerificationStatus.FAILED,
              },
            }));
          }
          setDocuments(data.getDriverUserById?.driverProfile?.documents ?? [])

          const frontDLsignedUrls = await getSignedUrl(data.getDriverUserById?.driverProfile?.license?.frontImage)
          const backDLsignedUrls = await getSignedUrl(data.getDriverUserById?.driverProfile?.license?.backImage)

          setFrontLicenseImage(frontDLsignedUrls || "");
          setBackLicenseImage(backDLsignedUrls || "");
        }
      }

    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  })

  const [
    verifyDriverLicenseDvla,
    { loading: loadingDVLAVerificationStatus },
  ] = useLazyQuery(VERIFY_DRIVER_LICENCE_DVLA, {
    fetchPolicy: "no-cache",
    onCompleted(data) {
      socket.emit(SOCKET_EVENTS.REQUEST_VIEW_USER_DRIVER, { driverId: values.id });
      setDvlaDialogVisible(false)
      snackbar({
        message: "DVLA license verification in progress. You will receive the status shortly.",
        variant: SnackBarVariant.INFORMATION,
      });
    },
    onError: error => {
      snackbar({
        message: 'Verification failed !',
        variant: SnackBarVariant.ERROR,
      });
    },
  });

  const initializeSocket = () => {
    if (!socket.connected) {
      socket.auth = { userId: userState.id };
      socket.connect();
    }

    // Ensure event listener is not duplicated
    socket.off(SOCKET_EVENTS.GET_UPDATED_USER_DRIVER);
    socket.on(SOCKET_EVENTS.GET_UPDATED_USER_DRIVER, handleUpdatedUserDriver);
  };

  const disconnectSocket = () => {
    if (socket.connected) {
      socket.off(SOCKET_EVENTS.GET_UPDATED_USER_DRIVER);
      socket.disconnect();
    }
  };

  const handleUpdatedUserDriver = async () => {
    if (!userId) return;
    await getUserDriver({ variables: { userId } });
  };

  // Cleanup WebSocket on Unmount
  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, []);

  const [deleteDriverDocument] = useMutation(DELETE_DRIVER_DOCUMENT, {
    fetchPolicy: "network-only",
    onCompleted: () => {
      snackbar({
        message: "Document deleted",
        variant: SnackBarVariant.SUCCESS
      });
      const docs = documents.filter((document) => document.documentKey !== deleteDocumentUrl);
      setDocuments(docs);
      setValues({
        ...values,
        documents: docs,
      });
      setDeleteInProgress(false);
      setOpenDeleteDialog(false);
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      setDeleteInProgress(false);
      setOpenDeleteDialog(false);
    }
  });

  const [addDriverDocumentHandler] = useMutation(ADD_DRIVER_DOCUMENT, {
    fetchPolicy: "network-only",
    onCompleted: (data: any) => {
      if (data?.addDriverDocument) {
        setValues((prevValues) => {
          const updatedDocs = [...(prevValues.documents ?? []), data.addDriverDocument];

          setDocuments(updatedDocs)

          return {
            ...prevValues,
            documents: updatedDocs,
          };
        });

        snackbar({
          message: "Document added successfully",
          variant: SnackBarVariant.SUCCESS,
        });
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      });
    },
  });


  const [updateDriverProfile, { loading: updateProfileLoading }] = useMutation(UPDATE_DRIVER_PROFILE, {
    fetchPolicy: "network-only",
    onCompleted(data) {
      snackbar({
        message: "Driver updated successfully",
        variant: SnackBarVariant.SUCCESS,
      });
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  })

  const [createDriverProfile, { loading: createProfileLoading }] = useMutation(CREATE_DRIVER_PROFILE, {
    fetchPolicy: "network-only",
    onCompleted(data) {
      snackbar({
        message: data.createDriverUserProfile.message,
        variant: SnackBarVariant.SUCCESS
      });
      navigate("/drivers")

    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  })


  useEffect(() => {
    if (userId) {
      getUserDriver({ variables: { userId } });
    }
  }, [userId]);

  function handleFormSubmit(values: IDriverProfile) {
    if (values.id) {
      const { id, documents, ...rest } = values
      updateDriverProfile({
        variables: {
          driverProfileId: values.id,
          driverUserInput: rest
        }
      })
    } else {
      createDriverProfile({
        variables: {
          id: userId,
          driverUserInput: values
        }
      })
    }


  };

  async function downloadDocument(documentUrl: string, documentName: string) {
    if (!userState.tenancy?.id) {
      return;
    }
    const { fileExtension } = checkDeleteOrDownloadFileType(documentUrl);
    const file = await getSignedUrl(documentUrl)
    const url: any = file;

    fetch(url, {
      method: "GET"
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${documentName}.${fileExtension}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  }

  async function uploadDriverImage(files: File[], type: string) {
    try {
      if (imageUploadInProgress) {
        return snackbar({
          message: "Image upload is in progress",
          variant: SnackBarVariant.ERROR
        });
      }
      const allowedMimeTypes = ["image/jpeg", "image/png", "image/jpg"];

      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        const extensionErrorMessage = await validateSingleFileExtension(file, allowedMimeTypes);
        if (extensionErrorMessage) {
          snackbar({
            message: extensionErrorMessage,
            variant: SnackBarVariant.ERROR
          });
          setFrontImageUpload(false);
          setBackImageUpload(false);
          setImageUploadInProgress(false)
          return;
        }

        // 5MB limit exceeded
        if (file.size > 5242880) {
          throw new Error("File size exceeded limit of 5MB");
        }
        setImageUploadInProgress(true);
        let key: string = "";
        const uniqueId = uuidv4();
        if (file.type === "image/jpeg") {
          key = `${userState.tenancy.id}/${uniqueId}-vehicle.jpeg`;
        } else if (file.type === "image/png") {
          key = `${userState.tenancy.id}/${uniqueId}-vehicle.png`;
        }
        if (_e_) {
          await _e_
            .add({
              name: key,
              file: file,
              complete: async () => {
                if (type === "FRONT") {
                  getDriverImage(key, type);
                  setValues({
                    ...values,
                    license: {
                      ...values.license,
                      frontImage: key
                    }
                  });
                } else if (type === "BACK") {
                  getDriverImage(key, type);
                  setValues({
                    ...values,
                    license: {
                      ...values.license,
                      backImage: key
                    }
                  });
                }
                snackbar({
                  message: "Image Uploaded Successfully",
                  variant: SnackBarVariant.SUCCESS
                });
                setFrontImageUpload(false);
                setBackImageUpload(false);
                setImageUploadInProgress(false)
              }
            });
        }
      }
    } catch (err: any) {
      setImageUploadInProgress(false);
      snackbar({
        message: formatGraphQLErrorMessage(err?.message),
        variant: SnackBarVariant.ERROR
      });
    }
  }

  const handleLicenseDialogOpen = () => {
    setDvlaDialogVisible(true);
    setDvlaVerificationStatus('');
  };

  const handleLicenseDialogClose = () => {
    setDvlaDialogVisible(false);
    setDvlaVerificationStatus('');
  };

  const handleDialogOpen = () => {
    setOpenDeleteDialog(true)
  }

  const handleDialogClose = () => {
    setOpenDeleteDialog(false);
  }

  const deleteButtonHandle = (url: string, index: number) => {
    setDeleteDocumentUrl(url);
    setDocumentIndex(index);
    handleDialogOpen();
  }

  const getDriverImage = async (url: string, type: string) => {
    if (url) {
      try {
        const file = await getSignedUrl(url);
        if (type === "FRONT") {
          setFrontLicenseImage(file)
        } else if (type === "BACK") {
          setBackLicenseImage(file)
        }
      } catch (error: any) {
        captureErrorException(error)
        snackbar({
          message: error,
          variant: SnackBarVariant.ERROR
        });
      }
    }
  };

  async function uploadDriverDocuments(
    file: File,
    title: string,
    documentType: string,
    reminderBefore: number,
    expiryDate: string,
  ) {
    try {
      if (!userState.tenancy?.id) {
        return;
      }
      // 50MB limit exceeded
      if (file.type === "application/pdf" && file.size > 52428800) {
        throw new Error("File size exceeded limit of 50MB");
      }
      if (isImageFileType(file.type) && file.size > 5242880) {
        throw new Error("File size exceeded limit of 5MB");
      }
      setUploadInProgress(true);
      const fileName = file.name.split(".")[0];
      if (checkUploadFileFormat(file.type)) {
        const {
          fileExtension,
        } = uploadFileExtensionAndContentType(file.type);
        const uniqueId = uuidv4();
        const key = `${userState.tenancy.id}/${uniqueId}.${fileExtension}`;

        if (_e_) {
          await _e_
            .add({
              name: key,
              file: file,
              complete: async () => {
                const newDocument = {
                  documentKey: key,
                  title,
                  documentName: fileName,
                  expiryDate,
                  documentType,
                  reminderBefore
                }
                setUploadedDocument(newDocument)
                snackbar({
                  message: "Document Added",
                  variant: SnackBarVariant.SUCCESS,
                });
                resetUploadState();
                if (values.id) {
                  await addDriverDocumentHandler({
                    variables: { driverProfileId: values.id, document: newDocument },
                  });
                } else {
                  const updatedDocuments = [...documents, newDocument];
                  setDocuments(updatedDocuments);
                  setValues({
                    ...values,
                    documents: updatedDocuments
                  })
                  snackbar({
                    message: "Document Added",
                    variant: SnackBarVariant.SUCCESS,
                  });
                  resetUploadState();
                }
              }
            });
        }
      } else {
        resetUploadState();
        return snackbar({
          message: "Please only upload PDF, PNG, JPG or JPEG files",
          variant: SnackBarVariant.ERROR
        });
      }
    } catch (err: any) {
      captureErrorException(err)
      snackbar({ message: err?.message, variant: SnackBarVariant.ERROR });
    }
  }

  // Remove the document
  function deleteDocument(url: string) {
    setDeleteInProgress(true);
    setDeleteDocumentUrl(url);
    if (values.id) {
      deleteDriverDocument({
        variables: {
          driverProfileId: values.id,
          documentKey: url
        }
      })
    } else {
      const docs = documents.filter((document) => document.documentKey !== deleteDocumentUrl);
      setDocuments(docs);
      const updatedDocuments = documents.filter((doc) => doc.documentKey !== url);
      setDocuments(updatedDocuments);
      setValues({
        ...values,
        documents: updatedDocuments,
      });
      snackbar({
        message: "Document Deleted",
        variant: SnackBarVariant.SUCCESS,
      });
      setDeleteInProgress(false);
      setOpenDeleteDialog(false);
    }
  }

  const resetUploadState = () => {
    setDocumentDialogVisible(false);
    setUploadInProgress(false);
    setUploadedDocument(null);
  };




  const verifyLicenceWithDvla = (data: IDvlaCheckValues) => {
    initializeSocket();
    verifyDriverLicenseDvla({
      variables: {
        ...data,
        driverId: values.id
      }
    });
    setDvlaVerificationStatus(DvlaVerificationStatus.PENDING);
  };



  const columns = [
    {
      label: "Document Type",
      name: "documentType",
    },
    {
      label: "Document Name/Title",
      name: "title"
    },
    {
      label: "Expiry Date",
      name: "expiryDate",
      options: {
        customBodyRender: (value: string) => value ? getLocalizedDateFormat(country, value, DATE_TYPE.CONDENSED) : "N/A"
      }
    },
    {
      label: "Actions",
      name: "actions",
      options: {
        filter: false,
        customBodyRender: (value: any) => {
          return (
            <React.Fragment>
              <div>
                <IconButton
                  edge="end"
                  aria-label="download"
                  onClick={() =>
                    downloadDocument(value.url, value.name)
                  }
                  size="large">
                  <CloudDownloadIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => { deleteButtonHandle(value.url, value.index) }}
                  size="large">
                  <DeleteIcon />
                </IconButton>
              </div>
            </React.Fragment>
          );
        }
      }
    }
  ];

  const getRows = (documents: IDriverDocument[]) => {
    if (!documents || documents === null) {
      return [];
    }
    const docs = [...documents]
    const sortedDocuments = returnSortedDocuments(docs)
    if (sortedDocuments && sortedDocuments.length > 0) {
      return sortedDocuments.map((driverDocument: IDriverDocument) => {
        return {
          ...driverDocument,
          actions: { url: driverDocument.documentKey, name: driverDocument.documentName },
          title: driverDocument.title || driverDocument.documentName,
          expiryDate: driverDocument.expiryDate,
          documentType: driverDocument.documentType,
          documentKey: driverDocument.documentKey
        };
      });
    } else {
      return []
    }
  };



  return (
    <Grid container item xs={12}>
      <Grid container item xs={12} alignItems="center">
        <Typography variant="h1" color="primary">
          Drivers
        </Typography>
        <Box color="white" sx={{ pr: 1 }}></Box>
        <DoubleArrowIcon />
        <Box color="white" sx={{ pl: 1 }}></Box>
        <Typography variant="h1" color="primary">
          &nbsp;Driver Details
        </Typography>
      </Grid>
      <Paper style={{ padding: '20px', width: '100%' }}>
        <Formik
          enableReinitialize
          initialValues={values}
          onSubmit={(values, { setSubmitting }) => {
            handleFormSubmit(values);
            setSubmitting(false);
          }}
        >
          {(formikProps) => (
            <Form style={{ width: '100%' }}>
              <Box
                sx={{
                  padding: 2
                }}>
                <Typography
                  sx={{
                    color: '#002F6E',
                    paddingBottom: 1,
                    paddingTop: 1,
                    fontSize: 18,
                  }}
                >
                  User Details
                </Typography>
                <Divider
                  sx={{ borderBottomWidth: 1, borderColor: '#D3D3D3' }}
                />
                <Grid container spacing={2} sx={{ paddingTop: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      component={TextField}
                      placeholder="First Name"
                      label="First Name"
                      name={"firstName"}
                      disabled
                      value={values.firstName}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setValues({
                          ...values,
                          firstName: event.target.value
                        })
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      component={TextField}
                      placeholder="Last Name"
                      label="Last Name"
                      name={"lastName"}
                      disabled
                      value={values.lastName}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setValues({
                          ...values,
                          lastName: event.target.value
                        })
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      component={TextField}
                      placeholder="Email"
                      label="Email"
                      name={"email"}
                      disabled
                      type="email"
                      value={values.email}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setValues({
                          ...values,
                          email: event.target.value
                        })
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl variant="outlined" fullWidth>
                      <FlatPickerBar
                        enableTime={false}
                        handleDateChange={(value: Date) => {
                          setValues({
                            ...values,
                            dateOfBirth: d.fromJSDate(value).toUTC().toISO(),
                          });
                        }}
                        label={"DOB"}
                        identifier={"dateOfBirth"}
                        placeholderValue={"DOB"}
                        value={getLocalizedDateFormat(country, values.dateOfBirth, DATE_TYPE.CONDENSED)}
                        maxDate={getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED)}
                        country={country}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      component={TextField}
                      name={"phoneNumber.phone"}
                      required
                    >
                      {({ form: { setFieldValue, setTouched, touched, errors }, meta }: FieldProps) => {
                        return (
                          <div className={`phone-input-container`}>
                            <label className={`${phoneError ? "input-error-label" : ""} phone-input-label`}>
                              Phone Number <sup className={"phone-input-required"}>*</sup>
                            </label>
                            <PhoneInput
                              international={false}
                              defaultCountry={countryCode}
                              placeholder="Enter phone number"
                              className={phoneError ? "phone-input-error" : ""}
                              value={values.phoneNumber.phone}
                              name={"phoneNumber.phone"}
                              required
                              disabled
                              onChange={(val: any) => {
                                if (val && isValidPhoneNumber(val)) {
                                  setPhoneError("");
                                }
                                setValues({
                                  ...values,
                                  phoneNumber: {
                                    ...values.phoneNumber,
                                    phone: val
                                  }
                                });
                              }}
                              onBlur={() => {
                                if (!values.phoneNumber.phone) {
                                  setPhoneError("Phone number is required.");
                                } else if (!isValidPhoneNumber(values.phoneNumber.phone)) {
                                  setPhoneError("Enter a valid phone number.");
                                } else {
                                  setPhoneError("");
                                }
                              }}
                              onCountryChange={(val) => {
                                setCountryCode(val);
                              }}
                            />
                            {
                              phoneError !== "" ?
                                <span className={"phone-error-message"}>{phoneError}</span>
                                : (meta.touched && meta.error) &&
                                <span className={"phone-error-message"}>
                                  {phoneError || "Phone number is required."}
                                </span>
                            }
                          </div>
                        );
                      }}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AddressPicker
                      fieldName={"address"}
                      onChange={(address: IAddress) => {
                        if (address) {
                          setValues({ ...values, address: address })
                        }
                      }}
                      addGeometry={true}
                    />
                  </Grid>
                </Grid>

                {/* License Section */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    paddingBottom: 1,
                    paddingTop: 4
                  }}
                >
                  <Typography
                    sx={{
                      color: '#002F6E',
                      fontSize: 18,
                      flex: 1,
                      minWidth: 150,
                    }}
                  >Driving{' '}
                    {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)}
                  </Typography>
                  {values.id && ['United Kingdom', 'UK'].includes(country || '') && values.license.dvlaVerificationStatus && (
                    <Chip
                      label={values.license.dvlaVerificationStatus}
                      sx={{
                        backgroundColor: values.license.dvlaVerificationStatus === DvlaVerificationStatus.SUCCEEDED ? 'green'
                          : values.license.dvlaVerificationStatus === DvlaVerificationStatus.PENDING ? 'orange'
                            : 'red',
                        color: '#fff',
                        fontWeight: 'bold',
                        marginRight: 2
                      }}
                    />
                  )}
                  {values.id && ['United Kingdom', 'UK'].includes(country || '') && (
                    <Fab
                      variant="extended"
                      size="medium"
                      aria-label="License Validate"
                      className="blackBackButton"
                      onClick={handleLicenseDialogOpen}
                    >
                      Validate {' '} {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} <RefreshIcon />
                    </Fab>
                  )}
                </Box>

                <Divider
                  sx={{ borderBottomWidth: 1, borderColor: '#D3D3D3' }}
                />

                <Grid container spacing={2} sx={{ paddingTop: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      component={TextField}
                      placeholder={`Driving ${getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Number`}
                      label={`Driving ${getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Number`}
                      name="license.licenseNumber"
                      fullWidth
                      inputProps={{ maxLength: 20 }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {

                        setValues((prevValues) => ({
                          ...prevValues,
                          license: {
                            ...prevValues.license,
                            licenseNumber: e.target.value,
                          },
                        }));
                      }}
                    />

                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl variant="outlined" fullWidth>
                      <FlatPickerBar
                        enableTime={false}
                        handleDateChange={(value: Date) => {
                          const license = {
                            ...values.license,
                            dateOfExpiry: d.fromJSDate(value).toUTC().toISO()
                          };
                          setValues({
                            ...values,
                            license: license
                          })
                        }}
                        label={`Driving ${getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Expiry Date`}
                        identifier={"dateOfExpiry"}
                        placeholderValue={`Driving ${getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Expiry Date`}
                        value={getLocalizedDateFormat(country, values.license?.dateOfExpiry, DATE_TYPE.CONDENSED)}
                        minDate={getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED)}
                        country={country}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      component={TextField}
                      placeholder={`${getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Issued By`}
                      label={`${getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Issued By`}
                      name="license.issuingAuthority"
                      fullWidth
                      inputProps={{ maxLength: 100 }}
                      validate={(value: string) => validateName(value, "Licence Issued By")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = e.target;

                        setValues((prevValues) => ({
                          ...prevValues,
                          license: {
                            ...prevValues.license,
                            issuingAuthority: value,
                          },
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Field
                      component={TextField}
                      placeholder="National Insurance Number"
                      label="National Insurance Number"
                      name={"nationalInsuranceNumber"}
                      value={values?.nationalInsuranceNumber}
                      inputProps={{ maxLength: 20 }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {

                        setValues((prevValues) => ({
                          ...prevValues,
                          nationalInsuranceNumber: e.target.value,
                        }));
                      }}
                      fullWidth
                    ></Field>
                  </Grid>
                  <Grid
                    item xs={12} sm={6} md={3}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Grid item xs={12} sm={6} md={6} sx={{ minWidth: 120, flexGrow: 1 }} >
                      <Field
                        component={TextField}
                        placeholder="Offences"
                        label="Offences"
                        name="license.offences"
                        value={values.license.offences}
                        type="number"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const value = parseInt(event.target.value);
                          if (value >= 0) {
                            const license = {
                              ...values.license,
                              offences: parseInt(event.target.value)
                            };
                            setValues({
                              ...values,
                              license: license
                            })
                          }
                          else {
                            snackbar({
                              message: "Negative value cannot be given",
                              variant: SnackBarVariant.ERROR
                            });
                          }
                        }
                        }
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} sx={{ minWidth: 120, flexGrow: 1 }}>
                      <Field
                        component={TextField}
                        placeholder="Points"
                        label="Points"
                        name="license.points"
                        value={values.license.points}
                        type="number"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const value = parseInt(event.target.value);
                          if (value >= 0) {
                            const license = {
                              ...values.license,
                              points: parseInt(event.target.value)
                            };
                            setValues({
                              ...values,
                              license: license
                            })
                          }
                          else {
                            snackbar({
                              message: "Negative value cannot be given",
                              variant: SnackBarVariant.ERROR
                            });
                          }
                        }
                        }
                        required
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} sx={{ paddingTop: 3 }}>
                    {/* Front License Image */}
                    <Grid item xs={12} sm={6}>
                      <Box textAlign="center">
                        <Typography variant="h5" fontWeight={600} color="primary">
                          Front {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Image
                        </Typography>
                        <Box mt={2}>
                          <Fab
                            className={`${styles.saveButton} imageUploadButton`}
                            variant="extended"
                            size="medium"
                            aria-label="Upload Front License Image"
                            onClick={() => setFrontImageUpload(true)}
                          >
                            Upload Image
                          </Fab>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          Upload the front side of your {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Supported formats: .png, .jpeg, .jpg
                        </Typography>
                        {frontLicenseImage && (
                          <Box mt={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <img
                              src={frontLicenseImage}
                              alt="Front License"
                              style={{
                                width: 180,
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 12,
                                border: '2px solid #ccc',
                                boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                              }}
                            />
                            <Typography variant="subtitle2" color="gray" sx={{ mt: 1 }}>
                              Preview - Front {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Back License Image */}
                    <Grid item xs={12} sm={6}>
                      <Box textAlign="center">
                        <Typography variant="h5" fontWeight={600} color="primary">
                          Back {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Image
                        </Typography>
                        <Box mt={2}>
                          <Fab
                            className={`${styles.saveButton} imageUploadButton`}
                            variant="extended"
                            size="medium"
                            aria-label="Upload Back License Image"
                            onClick={() => setBackImageUpload(true)}
                          >
                            Upload Image
                          </Fab>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          Upload the back side of your {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Supported formats: .png, .jpeg, .jpg
                        </Typography>
                        {backLicenseImage && (
                          <Box mt={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <img
                              src={backLicenseImage}
                              alt="Back License"
                              style={{
                                width: 180,
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 12,
                                border: '2px solid #ccc',
                                boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                              }}
                            />
                            <Typography variant="subtitle2" color="gray" sx={{ mt: 1 }}>
                              Preview - Back {getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Image Upload Dialogs */}
                    {isFrontImageUpload && (
                      <DragDropImages
                        open={isFrontImageUpload}
                        handleCloseDragDrop={() => setFrontImageUpload(false)}
                        handleSave={(files) => uploadDriverImage(files, "FRONT")}
                        filesLimit={1}
                      />
                    )}
                    {isBackImageUpload && (
                      <DragDropImages
                        open={isBackImageUpload}
                        handleCloseDragDrop={() => setBackImageUpload(false)}
                        handleSave={(files) => uploadDriverImage(files, "BACK")}
                        filesLimit={1}
                      />
                    )}
                  </Grid>

                </Grid>

                {/* Documents Section */}
                <Typography
                  sx={{
                    color: '#002F6E',
                    paddingBottom: 1,
                    paddingTop: 4,
                    fontSize: 18,
                  }}
                >
                  Add Driver Documents
                </Typography>
                <Divider
                  sx={{ borderBottomWidth: 1, borderColor: '#D3D3D3' }}
                />
                <Box mt={2}>
                  <Grid item xs={12} sm={6} md={2} lg={2} xl={2}>
                    <Fab
                      className="blackBackButton"
                      variant="extended"
                      size="medium"
                      aria-label="Update"
                      onClick={() => {
                        setDocumentDialogVisible(true);
                      }}
                    >
                      ADD DOCUMENT
                    </Fab>
                  </Grid>
                  <div>
                    <DocumentDialog
                      open={documentDialogVisible}
                      handleClose={() => setDocumentDialogVisible(false)}
                      uploadInProgress={uploadInProgress}
                      onSubmit={uploadDriverDocuments}
                      accept="image/jpg, image/jpeg, image/png, application/pdf"
                    />
                  </div>
                  <Grid item xs={12} sx={{ paddingTop: 4 }}>
                    <Paper variant="outlined">
                      <NuvvenTable
                        key={new Date().getMilliseconds()}
                        title={""}
                        rows={getRows(documents)}
                        columns={columns}
                        setSelection={() => { }}
                        options={{
                          selectableRows: "none",
                          download: false,
                          filter: false,
                          print: false,
                          search: false,
                          viewColumns: false,
                          elevation: 0
                        }}
                      />
                      {
                        openDeleteDialog && (
                          <ConfirmationDialog
                            isOpen={openDeleteDialog}
                            onCancel={handleDialogClose}
                            onConfirm={() => {
                              deleteDocument(deleteDocumentUrl);
                            }}
                            isInProgress={deleteInProgress}
                            title=""
                            description={"Are you sure, you want to delete this document?"}
                          />
                        )
                      }
                    </Paper>
                  </Grid>
                </Box>
                <Grid item xs={12} sx={{ paddingTop: 4 }}>
                  <Fab
                    variant="extended"
                    size="medium"
                    aria-label={values.id ? "update" : "create"}
                    style={{ marginLeft: 10 }}
                    type="submit"
                  >
                    {(updateProfileLoading || createProfileLoading) && (
                      <CircularProgress size={14} style={{ color: "white", marginRight: "10px" }} />
                    )}
                    Update
                  </Fab>
                </Grid>
                <DvlaVerificationDialog
                  title={"Check Licence"}
                  description={
                    "Please validate or enter following details to proceed for licence check. All fields are mandatory"
                  }
                  isOpen={dvlaDialogVisible}
                  onCancel={() => {
                    handleLicenseDialogClose()
                  }}
                  onConfirm={verifyLicenceWithDvla}
                  data={{
                    licenseNumber: values?.license?.licenseNumber || "",
                    insuranceNumber: values?.nationalInsuranceNumber || "",
                    postCode: values?.address?.zipcode || ""
                  }}
                  verificationStatus={dvlaVerificationStatus}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Grid >
  );
};


